// useMaintenanceSalaryCombinationV2.js
import { useState, useCallback, useEffect } from "react";
import maintenanceSalaryCombinationV2Service from "../services/maintenanceSalaryCombinationV2";
import operationBenefitsService from "../services/operationBenefitsService";
import { SetAuditTrail } from "../../components/Reports/AuditTrailReportEmployeeSetup";

export const useMaintenanceSalaryCombinationV2 = ({
  showGeneralMessage,
  apiFilterPayload = [],
  additionalFilters = [],
  user,
  isMainModule = true
}) => {
  const [loading, setLoading] = useState(false);
  const [allRules, setAllRules] = useState([]);
  const [allBenefitGroups, setAllBenefitGroups] = useState([]);
  const [rules, setRules] = useState([]);
  const [benefitGroups, setBenefitGroups] = useState([]);

  // Helper to extract error message from .NET ProblemDetails or Strings
  const extractErrorMessage = (err) => {
    const data = err.response?.data;
    if (!data) return "An unexpected error occurred.";

    // 1. If it's a simple string
    if (typeof data === 'string') return data;

    // 2. If it has a custom 'message' property
    if (data.message) return data.message;

    // 3. If it's a .NET Validation ProblemDetails object (errors: { Prop: ["Msg"] })
    if (data.errors) {
      const firstErrorKey = Object.keys(data.errors)[0];
      return data.errors[firstErrorKey][0];
    }

    // 4. Fallback to 'title' if it exists (standard for ProblemDetails)
    if (data.title) return data.title;

    return "Error performing action.";
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await maintenanceSalaryCombinationV2Service.getAll();
      setAllRules(res.data.rules || []);
      setAllBenefitGroups(res.data.groups || []);
    } catch (err) {
      showGeneralMessage("Failed to load data", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  useEffect(() => {
    if (!isMainModule) return; // Only fetch if it's the main module
    fetchData();
  }, [fetchData, isMainModule]);

  const fetchDashboard = useCallback(async () => {
    if (!apiFilterPayload || apiFilterPayload.length === 0) {
      setRules([]);
      setBenefitGroups([]);
      return;
    }

    setLoading(true);
    try {
      const res = await maintenanceSalaryCombinationV2Service.getDashboard(apiFilterPayload, additionalFilters);
      setRules(res.data.rules || []);
      setBenefitGroups(res.data.groups || []);
    } catch (err) {
      showGeneralMessage("Failed to load dashboard data", false);
    } finally {
      setLoading(false);
    }
  }, [apiFilterPayload, showGeneralMessage]);

  useEffect(() => {
    if (!isMainModule) return; // Only fetch if it's the main module
    fetchDashboard();
  }, [fetchDashboard, isMainModule]);

  const saveRateRule = async (id, payload) => {
    const updatedPayload = {
      ...payload,
      createdBy: user?.id ?? 0,
      updatedBy: user?.id ?? 0,
    }
    setLoading(true);
    try {
      id ? await maintenanceSalaryCombinationV2Service.update(id, updatedPayload)
        : await maintenanceSalaryCombinationV2Service.create(updatedPayload);

      await SetAuditTrail({
        User_Id: user.id,
        Action: id ? "UPDATE" : "ADD",
        Module: "Salary Module V2",
        Feature: "Rate Rule",
        Description: `${id ? 'Updated' : 'Added'} rate category: ${payload.category}`
      });

      showGeneralMessage("Rate Rule saved", true);
      fetchDashboard();
      return { success: true };
    } catch (err) {
      // MODIFIED: Use the extraction helper to avoid passing objects to the UI
      showGeneralMessage(extractErrorMessage(err), false);
      return { success: false };
    } finally { setLoading(false); }
  };

  const saveBenefit = async (id, payload, createdById) => {
    setLoading(true);

    const updatedPayload = {
      ...payload,
      createdBy: createdById ?? 0,
      updatedBy: createdById ?? 0,
    }

    try {
      id ? await operationBenefitsService.update(id, updatedPayload)
        : await operationBenefitsService.create(updatedPayload);

      await SetAuditTrail({
        User_Id: user.id,
        Action: id ? "UPDATE" : "ADD",
        Module: "Salary Module V2",
        Feature: "Operation Benefit",
        Description: `${id ? 'Updated' : 'Added'} benefit`
      });

      showGeneralMessage("Operation Benefit saved", true);
      fetchDashboard();
      return { success: true };
    } catch (err) {
      // MODIFIED: Use the extraction helper to avoid passing objects to the UI
      showGeneralMessage(extractErrorMessage(err), false);
      return { success: false };
    } finally { setLoading(false); }
  };

  const uploadBulkData = async (payload, createdById) => {
    setLoading(true);

    const updatedPayload = {
      ...payload,
      createdBy: createdById ?? 0,
      updatedBy: createdById ?? 0,
    }

    try {
      await maintenanceSalaryCombinationV2Service.uploadBulk(updatedPayload);

      await SetAuditTrail({
        User_Id: user.id,
        Action: "UPLOAD",
        Module: "Maintenance Salary Combination",
        Feature: "Bulk Import",
        Description: `Uploaded bulk configuration (Rules: ${payload.mscRules.length}, Benefits: ${payload.benefits.length})`
      });

      showGeneralMessage("Bulk configuration uploaded successfully", true);
      fetchDashboard();
      return { success: true };
    } catch (err) {
      showGeneralMessage(extractErrorMessage(err), false);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  return {
    rules, benefitGroups, loading,
    saveRateRule,
    saveBenefit,
    fetchMSCData: fetchData,
    allRules,
    allBenefitGroups,
    refresh: fetchDashboard,
    showGeneralMessage,
    uploadBulkData // ADDED
  };
};